'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useDarkMode } from '@/components/DarkModeProvider';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import Footer from '@/components/Footer';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import TwoFactorVerify from '@/components/TwoFactorVerify';

type AuthMode = 'login' | 'signup';

interface ValidationErrors {
  fullName?: string;
  email?: string;
  password?: string;
}

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

function validateEmail(email: string): string | null {
  if (!email) return null;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address';
  }
  return null;
}

function validatePassword(password: string, mode: AuthMode): string | null {
  if (!password) return null;
  if (mode === 'signup') {
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
  }
  return null;
}

function calculatePasswordStrength(password: string): PasswordStrength {
  if (!password) return { score: 0, label: '', color: '' };
  
  let score = 0;
  
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  
  if (score <= 2) return { score, label: 'Weak', color: '#DC2626' };
  if (score <= 3) return { score, label: 'Fair', color: '#F59E0B' };
  if (score <= 4) return { score, label: 'Good', color: '#10B981' };
  return { score, label: 'Strong', color: '#059669' };
}

function validateFullName(name: string): string | null {
  if (!name) return null;
  if (name.trim().length < 2) {
    return 'Name must be at least 2 characters';
  }
  return null;
}

export default function AuthClient() {
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { signUp, signIn, signInWithGoogle, signOut } = useAuth();
  const { showSuccess, showError } = useToast();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [show2FAVerify, setShow2FAVerify] = useState(false);
  const [pendingUser, setPendingUser] = useState<any>(null);
  const [isSigningUp, setIsSigningUp] = useState(false);

  const passwordStrength = mode === 'signup' ? calculatePasswordStrength(password) : null;

  // Check for error query parameter from OAuth callback
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      const decodedError = decodeURIComponent(errorParam);
      setError(decodedError);
      showError(decodedError);
      // Clean up URL by removing error parameter
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('error');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [searchParams, showError]);

  const handleBlur = (field: string) => {
    setTouched({ ...touched, [field]: true });
    validateField(field);
  };

  const validateField = (field: string) => {
    const errors: ValidationErrors = { ...validationErrors };

    switch (field) {
      case 'fullName':
        const nameError = validateFullName(fullName);
        if (nameError) errors.fullName = nameError;
        else delete errors.fullName;
        break;
      case 'email':
        const emailError = validateEmail(email);
        if (emailError) errors.email = emailError;
        else delete errors.email;
        break;
      case 'password':
        const passwordError = validatePassword(password, mode);
        if (passwordError) errors.password = passwordError;
        else delete errors.password;
        break;
    }

    setValidationErrors(errors);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate all fields
    const errors: ValidationErrors = {};
    if (mode === 'signup') {
      const nameError = validateFullName(fullName);
      if (nameError) errors.fullName = nameError;
    }
    const emailError = validateEmail(email);
    if (emailError) errors.email = emailError;
    const passwordError = validatePassword(password, mode);
    if (passwordError) errors.password = passwordError;

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setTouched({ fullName: true, email: true, password: true });
      return;
    }

    setLoading(true);

    try {
      if (mode === 'signup') {
        setIsSigningUp(true);
        await signUp(email, password, { fullName });
        setSuccess('Account created successfully! Please sign in.');
        showSuccess('Account created successfully! Please sign in with your credentials.');
        setPassword('');
        setFullName('');
        setLoading(false);
        setTimeout(() => {
          setMode('login');
          setSuccess('');
          setIsSigningUp(false);
        }, 1500);
        return;
      } else {
        const result = await signIn(email, password);
        
        if (result.needsTwoFactor) {
          setPendingUser(result.user);
          setShow2FAVerify(true);
          return;
        }
        
        showSuccess('Signed in successfully! Welcome back.');
        router.push('/account-dashboard');
        return;
      }
    } catch (err: any) {
      if (isSchemaError(err)) {
        console.error('Schema error:', err.message);
        throw err;
      } else {
        let errorMessage = err?.message || 'An error occurred. Please try again.';
        if (errorMessage.includes('Signups not allowed') || errorMessage.includes('signup_disabled')) {
          errorMessage = 'Account registration is temporarily unavailable. Please contact support or try again later.';
        } else if (errorMessage.includes('already been registered') || errorMessage.includes('already exists')) {
          errorMessage = 'An account with this email already exists. Please sign in instead.';
        }
        setError(errorMessage);
        showError(errorMessage);
      }
      setLoading(false);
    }
  };

  const handleOAuth = async (provider: 'google') => {
    try {
      setLoading(true);
      setError('');
      
      if (provider === 'google') {
        // signInWithOAuth will redirect to Google, then back to /auth/callback
        // Errors are handled via the callback route which redirects with error query param
        await signInWithGoogle();
        // If we reach here without redirect, there was an immediate error
        // (e.g., provider not configured)
        setLoading(false);
      }
    } catch (err: any) {
      console.error('OAuth error:', err);
      setLoading(false);
      
      if (isSchemaError(err)) {
        console.error('Schema error:', err.message);
        throw err;
      } else {
        // Handle different types of OAuth errors
        let errorMessage = 'Google sign-in failed. Please try again.';
        
        if (err?.message) {
          errorMessage = err.message;
        } else if (err?.error_description) {
          errorMessage = err.error_description;
        } else if (typeof err === 'string') {
          errorMessage = err;
        }
        
        // Check for specific error types and provide helpful messages
        if (errorMessage.includes('popup') || errorMessage.includes('blocked')) {
          errorMessage = 'Popup was blocked. Please allow popups for this site and try again.';
        } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (errorMessage.includes('cancelled') || errorMessage.includes('denied')) {
          errorMessage = 'Sign-in was cancelled. Please try again if you want to continue.';
        } else if (errorMessage.includes('Unable to exchange external code') || errorMessage.includes('exchange external code')) {
          errorMessage = 'OAuth configuration error: The redirect URL in Google OAuth settings does not match. Please check your Google Cloud Console configuration.';
        } else if (errorMessage.includes('provider') || errorMessage.includes('not configured') || errorMessage.includes('disabled') || errorMessage.includes('not enabled') || errorMessage.includes('Unsupported provider')) {
          errorMessage = 'Google sign-in is not enabled. Please use email/password sign-in instead, or contact support to enable Google sign-in.';
        }
        
        setError(errorMessage);
        showError(errorMessage);
      }
    }
  };

  const handleModeSwitch = (newMode: AuthMode) => {
    setMode(newMode);
    setError('');
    setSuccess('');
    setValidationErrors({});
    setTouched({});
  };

  const handle2FAVerified = () => {
    setShow2FAVerify(false);
    setPendingUser(null);
    showSuccess('Signed in successfully! Welcome back.');
    router.push('/account-dashboard');
  };

  const handle2FACancel = async () => {
    setShow2FAVerify(false);
    setPendingUser(null);
    // Sign out the pending session
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  if (show2FAVerify) {
    return (
      <div style={{ background: 'var(--bg-primary)', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <main className="flex-1 pt-16 flex items-center justify-center py-12 px-4">
          <TwoFactorVerify onVerified={handle2FAVerified} onCancel={handle2FACancel} />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <main className="flex-1 pt-16 flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <AppLogo size={48} />
            </div>
            <h1 className="font-display font-medium text-3xl mb-1" style={{ color: 'var(--text-primary)' }}>
              {mode === 'login' ? 'Welcome back' : 'Create your account'}
            </h1>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {mode === 'login' ?'Sign in to access your purchases and downloads.' :'Join 48,000+ creators building with TemplateMarket.'}
            </p>
          </div>

          {/* Card */}
          <div
            className="rounded-2xl p-7"
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-lg)' }}
          >
            {/* Mode toggle */}
            <div
              className="flex p-1 rounded-xl mb-6"
              style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}
            >
              <button
                onClick={() => handleModeSwitch('login')}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  mode === 'login' ? 'shadow-sm' : ''
                }`}
                style={{
                  background: mode === 'login' ? 'var(--bg-card)' : 'transparent',
                  color: mode === 'login' ? 'var(--text-primary)' : 'var(--text-muted)',
                }}
              >
                Sign In
              </button>
              <button
                onClick={() => handleModeSwitch('signup')}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  mode === 'signup' ? 'shadow-sm' : ''
                }`}
                style={{
                  background: mode === 'signup' ? 'var(--bg-card)' : 'transparent',
                  color: mode === 'signup' ? 'var(--text-primary)' : 'var(--text-muted)',
                }}
              >
                Sign Up
              </button>
            </div>

            {/* OAuth buttons */}
            <div className="space-y-3 mb-6">
              <button
                type="button"
                onClick={() => handleOAuth('google')}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl text-sm font-semibold transition-all"
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-primary)',
                }}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>
            </div>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t" style={{ borderColor: 'var(--border)' }} />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2" style={{ background: 'var(--bg-card)', color: 'var(--text-muted)' }}>
                  Or continue with email
                </span>
              </div>
            </div>

            {/* Demo credentials notice */}
            <div
              className="flex items-start gap-2.5 p-3 rounded-xl text-xs mb-5"
              style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
            >
              <Icon name="InformationCircleIcon" size={16} />
              <div>
                <strong>Demo credentials:</strong>
                <br />
                Admin: admin@example.com / password123
                <br />
                User: user@example.com / password123
              </div>
            </div>

            {/* Success Message */}
            {success && (
              <div
                className="flex items-center gap-2.5 p-3 rounded-xl text-sm mb-5"
                style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#16A34A' }}
              >
                <Icon name="CheckCircleIcon" size={16} />
                {success}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4" suppressHydrationWarning>
              {mode === 'signup' && (
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                    Full Name
                  </label>
                  <input
                    type="text"
                    placeholder="Jordan Mitchell"
                    value={fullName}
                    onChange={(e) => {
                      setFullName(e.target.value);
                      if (touched.fullName) validateField('fullName');
                    }}
                    onBlur={() => handleBlur('fullName')}
                    className={`input-field ${touched.fullName && validationErrors.fullName ? 'border-red-500' : ''}`}
                    autoComplete="name"
                  />
                  {touched.fullName && validationErrors.fullName && (
                    <p className="text-xs mt-1.5 flex items-center gap-1" style={{ color: '#DC2626' }}>
                      <Icon name="ExclamationCircleIcon" size={12} />
                      {validationErrors.fullName}
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (touched.email) validateField('email');
                  }}
                  onBlur={() => handleBlur('email')}
                  className={`input-field ${touched.email && validationErrors.email ? 'border-red-500' : ''}`}
                  autoComplete="email"
                  required
                />
                {touched.email && validationErrors.email && (
                  <p className="text-xs mt-1.5 flex items-center gap-1" style={{ color: '#DC2626' }}>
                    <Icon name="ExclamationCircleIcon" size={12} />
                    {validationErrors.email}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>
                    Password
                  </label>
                  {mode === 'login' && (
                    <button type="button" className="text-xs font-medium" style={{ color: 'var(--accent)' }}>
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={mode === 'signup' ? 'Min. 8 characters' : '••••••••'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (touched.password) validateField('password');
                    }}
                    onBlur={() => handleBlur('password')}
                    className={`input-field pr-10 ${touched.password && validationErrors.password ? 'border-red-500' : ''}`}
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'var(--text-muted)' }}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                  </button>
                </div>
                {touched.password && validationErrors.password && (
                  <p className="text-xs mt-1.5 flex items-center gap-1" style={{ color: '#DC2626' }}>
                    <Icon name="ExclamationCircleIcon" size={12} />
                    {validationErrors.password}
                  </p>
                )}
                {mode === 'signup' && password && passwordStrength && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Password strength:</span>
                      <span className="text-xs font-semibold" style={{ color: passwordStrength.color }}>
                        {passwordStrength.label}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full" style={{ background: 'var(--bg-secondary)' }}>
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{
                          width: `${(passwordStrength.score / 5) * 100}%`,
                          background: passwordStrength.color,
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {mode === 'signup' && (
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="custom-checkbox mt-0.5" required />
                  <span className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                    I agree to the{' '}
                    <Link href="#" className="underline" style={{ color: 'var(--accent)' }}>
                      Terms of Service
                    </Link>
                    {' '}and{' '}
                    <Link href="#" className="underline" style={{ color: 'var(--accent)' }}>
                      Privacy Policy
                    </Link>
                  </span>
                </label>
              )}

              {/* Error */}
              {error && (
                <div
                  className="flex items-center gap-2.5 p-3 rounded-xl text-sm"
                  style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626' }}
                >
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !!success}
                className="btn-primary w-full justify-center py-3 text-base mt-2"
                style={{ opacity: loading || success ? 0.7 : 1 }}
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    {mode === 'login' ? 'Signing in...' : 'Creating account...'}
                  </>
                ) : mode === 'login' ? (
                  'Sign In'
                ) : (
                  'Create Account'
                )}
              </button>
            </form>
          </div>

          {/* Footer links */}
          <div className="text-center mt-6">
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}{' '}
              <button
                onClick={() => handleModeSwitch(mode === 'login' ? 'signup' : 'login')}
                className="font-semibold"
                style={{ color: 'var(--accent)' }}
              >
                {mode === 'login' ? 'Sign up for free' : 'Sign in'}
              </button>
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}